import styled from "@emotion/styled";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  MANAGE_USER_SCHEMA_DEFAULT_VALUES,
  ManageUserFormSchema,
  getManageUserFormSchema,
} from "./manage-user-form-helpers";
import { INDIVIDUAL_PLAN_ID } from "@shared/utils/constants";
import { TextField } from "../../shared/components/TextField";
import { Form } from "../../shared/components/Form";
import { useGetUser } from "../../queries/users/useGetUser";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useEditUser } from "../../queries/users/useEditUser";
import { FormErrorMessage } from "../../shared/components/FormErrorMessage";
import { EnableDisableUserSwitch } from "@features/Users/EnableDisableUserSwitch";
import { extendedTimezones, timezones } from "@shared/utils/data/timezones";
import { usaStates } from "@shared/utils/data/usaStates";
import { PhoneNumberTextField } from "@shared/components/PhoneNumberTextField";
import { Checkbox, SubmitButton } from "@avenue-8/sidekick-ui";
import { AdminDeleteUser } from "@features/Users/AdminDeleteUser";
import { useDeleteUser } from "@queries/users/useDeleteUser";
import { useGetSubscriptionPlans } from "@queries/subscription-plans/useGetSubscriptionPlans";
import { EditUserEmailModal } from "./EditUserEmailModal";
import { formatInternationalPhoneNumber } from "@shared/utils/form-converters";

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  padding: 2rem;
  border-radius: 1rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  width: 100%;

  ${(p) => p.theme.media.mobileDown} {
    padding: 1.5rem 1rem;
    gap: 1.5rem;
    grid-template-columns: 1fr;

    > :last-child {
      order: -1;
    }
  }
`;

const RightPanel = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
`;

type Props = {
  userId: string;
};

export function ManageUser(props: Props) {
  const { userId } = props;
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [editUserEmailModalOpen, setEditUserEmailModalOpen] = useState(false);

  const userQuery = useGetUser({
    id: userId,
    onError: () => {
      navigate("/admin/users");
    },
  });

  const user = userQuery.data;
  // Defaults to requiring an MLS provider while the user hasn't loaded yet -
  // only individual-plan users are exempt, so that's the stricter/safer default.
  const requiresMlsProvider = user?.subscriptionPlanId !== INDIVIDUAL_PLAN_ID;

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    control,
    reset,
  } = useForm<ManageUserFormSchema>({
    defaultValues: {
      ...MANAGE_USER_SCHEMA_DEFAULT_VALUES,
    },
    resolver: yupResolver(
      getManageUserFormSchema({ requireMlsProvider: requiresMlsProvider })
    ),
    reValidateMode: "onChange",
  });

  const subscriptionPlansQuery = useGetSubscriptionPlans();
  const subscriptionPlans = subscriptionPlansQuery.data?.items || [];

  const userCurrentSubscriptionPlan = subscriptionPlans.find(
    (plan) => plan.id === user?.subscriptionPlanId
  );
  const userAllowedProviders = useMemo(() => {
    return userCurrentSubscriptionPlan?.access.allowedMlsProviders || [];
  }, [userCurrentSubscriptionPlan?.access.allowedMlsProviders]);

  useEffect(() => {
    if (!user) return;
    reset({
      name: user.name,
      timezone: !user.timezone ? "auto-detect" : user.timezone,
      countryState: user.countryState,
      phoneNumber: formatInternationalPhoneNumber(user.phoneNumber),
      mlsId: user.mlsId ?? "",
      enabledMlsProvidersSelected:
        user.enabledMlsProviders || userAllowedProviders || [],
    });
  }, [user, reset, userAllowedProviders]);

  const editUserMutation = useEditUser();

  const onSubmit = async (data: ManageUserFormSchema) => {
    if (!isDirty) return;
    const areEnabledAndAllowedEqual = userAllowedProviders.every((p) =>
      data.enabledMlsProvidersSelected?.includes(p)
    );
    await editUserMutation.mutateAsync({
      userId,
      name: data.name,
      timezone: data.timezone === "auto-detect" ? undefined : data.timezone,
      countryState: data.countryState,
      phoneNumber: data.phoneNumber,
      mlsId: data.mlsId,
      enabledMlsProviders: areEnabledAndAllowedEqual
        ? null
        : data.enabledMlsProvidersSelected,
    });
    reset(data);
  };

  const deleteUserMutation = useDeleteUser();

  const onDeleteUser = async (data: { userId: string }) => {
    await deleteUserMutation.mutateAsync(data);
    navigate("/admin/users");
  };

  const isExtendedTimezones =
    search.get("EXTENDED_TIMEZONES")?.toLowerCase() === "true";

  const handleClickEditEmail = () => {
    setEditUserEmailModalOpen(true);
  };

  return (
    <Container>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        aria-disabled={userQuery.isLoading || deleteUserMutation.isPending}
        maxWidth={"700px"}
      >
        <Grid container columnSpacing={2}>
          <Grid item sm={6} xs={12}>
            <TextField
              name="name"
              label="Name"
              inputProps={{ ...register("name") }}
              fieldError={errors.name}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              name="email"
              label="E-mail"
              value={user?.email ?? ""}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="start">
                    <IconButton size="small" onClick={handleClickEditEmail}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Grid container columnSpacing={2}>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="countryState-label">State</InputLabel>
              <Controller
                name="countryState"
                control={control}
                render={({ field }) => (
                  <Select
                    label="State"
                    labelId="countryState-label"
                    {...field}
                    error={!!errors.countryState?.message}
                  >
                    {usaStates.map((state) => (
                      <MenuItem
                        key={state.abbreviation}
                        value={state.abbreviation}
                      >
                        {state.abbreviation}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.countryState?.message ?? " "}
              </FormErrorMessage>
            </FormControl>
          </Grid>
          <Grid item sm={6} xs={12}>
            <FormControl fullWidth>
              <InputLabel id="timezone-label">Time Zone</InputLabel>
              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <Select label="Time Zone" {...field}>
                    <MenuItem key={"auto-detect"} value={"auto-detect"}>
                      Detect automatically
                    </MenuItem>
                    {timezones.map((timezone) => (
                      <MenuItem key={timezone.value} value={timezone.value}>
                        {timezone.label}
                      </MenuItem>
                    ))}
                    {isExtendedTimezones && (
                      <ListSubheader>Extended time zones</ListSubheader>
                    )}
                    {isExtendedTimezones &&
                      extendedTimezones.map((timezone) => (
                        <MenuItem key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              <FormErrorMessage>
                {errors.timezone?.message ?? " "}
              </FormErrorMessage>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container columnSpacing={2}>
          <Grid item sm={6} xs={12}>
            <TextField
              name="mlsId"
              label="License number"
              inputProps={{ ...register("mlsId") }}
              fieldError={errors.mlsId}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <PhoneNumberTextField
              name="phoneNumber"
              label="Phone number (optional)"
              inputProps={{ ...register("phoneNumber") }}
              fieldError={errors.phoneNumber}
            />
          </Grid>
        </Grid>

        <TextField
          name="officeName"
          label="Brokerage name"
          value={user?.officeName ?? ""}
          InputProps={{ readOnly: true }}
        />

        <FormControl>
          <InputLabel>Email confirmed</InputLabel>
          <FormControlLabel
            control={
              <Switch checked={Boolean(user?.emailConfirmed)} disabled />
            }
            label={
              user?.emailConfirmed ? (
                <Typography variant="body2">
                  Email is marked as <strong>verified</strong>
                </Typography>
              ) : (
                <Typography variant="body2">
                  Email is marked as <strong>not verified</strong>
                </Typography>
              )
            }
          />
        </FormControl>

        <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
          <InputLabel id="subscriptionPlanId-label">
            Subscription plan
          </InputLabel>

          {subscriptionPlansQuery.isLoading && (
            <Skeleton
              variant="rounded"
              component={Select}
              height={66}
              value=""
            />
          )}

          {!subscriptionPlansQuery.isLoading && (
            <Select
              label="Subscription plan"
              labelId="subscriptionPlanId-label"
              readOnly
              value={user?.subscriptionPlanId ?? ""}
              IconComponent={() => <></>}
            >
              {subscriptionPlans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  <Box sx={{ cursor: "default" }}>
                    <Typography>{plan.name}</Typography>
                    <Typography variant="body2">
                      {plan.publicDescription}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="enabledMlsProvidersSelected-label">
            Enabled MLS Providers
          </InputLabel>
          <Controller
            name="enabledMlsProvidersSelected"
            control={control}
            render={({ field }) => (
              <Select
                label="Enabled MLS Providers"
                labelId="enabledMlsProvidersSelected-label"
                multiple
                renderValue={(selected) => selected.join(", ")}
                {...field}
                error={!!errors.enabledMlsProvidersSelected?.message}
              >
                {userAllowedProviders.map((mlsProvider) => (
                  <MenuItem key={`provider-${mlsProvider}`} value={mlsProvider}>
                    <Checkbox checked={field.value?.includes(mlsProvider)} />
                    {mlsProvider}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <FormErrorMessage>
            {errors.enabledMlsProvidersSelected?.message ?? " "}
          </FormErrorMessage>
        </FormControl>

        <SubmitButton
          isLoading={editUserMutation.isPending}
          loadingText="Saving..."
          sx={{ justifySelf: "flex-start", minWidth: "160px" }}
          disabled={!isDirty}
        >
          Save
        </SubmitButton>
        <Divider sx={{ margin: "2rem 0 1rem 0" }}>
          <Typography variant="body2">Danger zone</Typography>
        </Divider>

        {!!user && (
          <AdminDeleteUser
            userName={user.name}
            userEmail={user.email}
            userId={userId}
            onDeleteUser={onDeleteUser}
            isDeleting={deleteUserMutation.isPending}
          />
        )}
      </Form>
      <RightPanel>
        <Form aria-disabled={userQuery.isLoading}>
          <Box>
            <FormControl sx={{ textAlign: "center" }}>
              <FormLabel>Active</FormLabel>
              <EnableDisableUserSwitch
                userId={userId}
                userEnabled={user?.enabled ?? false}
                key={`${user?.id}-${user?.enabled}`}
              />
            </FormControl>
          </Box>
        </Form>
      </RightPanel>
      {!!user && (
        <EditUserEmailModal
          open={editUserEmailModalOpen}
          onClose={() => setEditUserEmailModalOpen(false)}
          userEmail={user.email}
          userName={user.name}
          userId={user.id}
        />
      )}
    </Container>
  );
}
